import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Webcam from 'react-webcam';
import BarcodeScanner from '../components/barcode/BarcodeScanner';
import Button from '../components/forms/Button';
import './BarcodeScanPage.css';

function BarcodeScanPage() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [scanMode, setScanMode] = useState('realtime'); // 'realtime', 'photo', or 'manual'
  const [manualBarcode, setManualBarcode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [productData, setProductData] = useState(null);
  const [isProcessingBarcode, setIsProcessingBarcode] = useState(false); // Flag to prevent duplicate API calls

  // Handle barcode detection from real-time scanner with debouncing
  const handleBarcodeDetected = useCallback(async (barcode) => {
    // Prevent duplicate API calls
    if (isProcessingBarcode) {
      return;
    }
    
    setIsProcessingBarcode(true);
    lookupBarcode(barcode);
  }, [isProcessingBarcode]);

  // Handle manual barcode entry
  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualBarcode.trim() && !isProcessingBarcode) {
      setIsProcessingBarcode(true);
      lookupBarcode(manualBarcode.trim());
    }
  };

  // Lookup barcode using the API
  const lookupBarcode = async (barcode) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Call the backend to lookup the barcode
      const response = await axios.post('/api/inventory/lookup-barcode', {
        barcode: barcode
      });
      
      // Check if the lookup was successful
      if (response.data.success) {
        setProductData({
          ...response.data,
          barcode: barcode // Ensure barcode is included in the data
        });
        setScanMode('result');
      } else {
        setError(`Product not found for barcode ${barcode}. ${response.data.message || ''}`);
      }
    } catch (error) {
      console.error('Error looking up barcode:', error);
      setError(error.response?.data?.message || 'Failed to lookup barcode. Please try again.');
    } finally {
      setIsLoading(false);
      // Reset processing flag after a delay to prevent immediate re-scanning
      setTimeout(() => {
        setIsProcessingBarcode(false);
      }, 2000);
    }
  };

  // Capture image from webcam
  const captureImage = () => {
    if (webcamRef.current && !isProcessingBarcode) {
      const imageSrc = webcamRef.current.getScreenshot();
      processImageForBarcode(imageSrc);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && !isProcessingBarcode) {
      const reader = new FileReader();
      reader.onloadend = () => {
        processImageForBarcode(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Convert base64 image to blob for upload
  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new Blob([ab], { type: mimeString });
  };

  // Process the captured image for barcode detection
  const processImageForBarcode = async (imageSrc) => {
    setIsLoading(true);
    setError('');
    setIsProcessingBarcode(true);
    
    try {
      // Create form data with the image
      const formData = new FormData();
      const imageBlob = dataURItoBlob(imageSrc);
      formData.append('image', imageBlob, 'barcode.jpg');
      
      // Call the backend API to scan the barcode
      const response = await axios.post('/api/inventory/scan-barcode', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Check if scanning was successful
      if (response.data.results && response.data.results.success) {
        setProductData(response.data.results);
        setScanMode('result');
      } else {
        setError('Could not detect a valid barcode in the image. Please try again with a clearer image.');
      }
    } catch (error) {
      console.error('Error scanning barcode from image:', error);
      setError(error.response?.data?.message || 'Failed to process image. Please try again.');
    } finally {
      setIsLoading(false);
      // Reset processing flag after a delay
      setTimeout(() => {
        setIsProcessingBarcode(false);
      }, 2000);
    }
  };

  // Navigate to Add Item page with pre-populated data
  const handleAddToInventory = () => {
    if (!productData) return;
    
    // Prepare data for the inventory form
    const formData = {
      item_name: productData.product_name || '',
      brand: productData.brand || '',
      item_weight: productData.item_weight || '',
      item_weight_unit: productData.item_weight_unit || '',
      image_url: productData.image_url || '',
      // Extract category from raw data if available
      category: productData.raw_data?.categories || '',
      // Default values for other required fields
      quantity: 1,
      unit_of_measure: productData.item_weight_unit ? productData.item_weight_unit : 'pcs',
      // Add notes with barcode information
      notes: productData.barcode ? `Barcode: ${productData.barcode}` : '',
      // Set today as purchase date
      purchase_date: new Date().toISOString().split('T')[0]
    };
    
    // Try to extract expiry date from raw data if available
    if (productData.raw_data?.expiry_date) {
      formData.expiry_date = productData.raw_data.expiry_date;
    }
    
    // Navigate to add item form with pre-populated data
    navigate('/inventory/add', { state: { prefillData: formData } });
  };

  // Reset and try again
  const handleScanAgain = () => {
    setScanMode('realtime');
    setProductData(null);
    setError('');
    setManualBarcode('');
    setIsProcessingBarcode(false);
  };

  // Close scanner and go back to inventory
  const handleClose = () => {
    navigate('/inventory');
  };

  // Render different scan modes
  const renderScanMode = () => {
    switch (scanMode) {
      case 'realtime':
        return (
          <div>
            <BarcodeScanner 
              onDetected={handleBarcodeDetected} 
              onClose={handleClose}
            />
            <div className="scan-options">
              <Button onClick={() => setScanMode('photo')} className="btn-secondary">
                <i className="fas fa-camera"></i> Take Photo
              </Button>
              <Button onClick={() => setScanMode('manual')} className="btn-secondary">
                <i className="fas fa-keyboard"></i> Enter Manually
              </Button>
            </div>
          </div>
        );
        
      case 'photo':
        return (
          <div>
            <div className="camera-container">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="camera-view"
              />
            </div>
            
            <div className="camera-controls">
              <Button 
                onClick={captureImage} 
                className="btn-primary"
                disabled={isProcessingBarcode}
              >
                <i className="fas fa-camera"></i> Capture Photo
              </Button>
              <Button 
                onClick={triggerFileInput} 
                className="btn-secondary"
                disabled={isProcessingBarcode}
              >
                <i className="fas fa-upload"></i> Upload Image
              </Button>
              <Button onClick={() => setScanMode('realtime')} className="btn-secondary">
                <i className="fas fa-barcode"></i> Real-time Scan
              </Button>
              <Button onClick={handleClose} className="btn-secondary">
                <i className="fas fa-times"></i> Cancel
              </Button>
            </div>
            
            <div className="file-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                ref={fileInputRef}
                className="file-upload-input"
                disabled={isProcessingBarcode}
              />
            </div>
          </div>
        );
        
      case 'manual':
        return (
          <div className="manual-entry">
            <h2>Enter Barcode Manually</h2>
            <form onSubmit={handleManualSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  placeholder="Enter barcode number"
                  className="barcode-input"
                  disabled={isProcessingBarcode}
                />
              </div>
              <div className="button-group">
                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isProcessingBarcode || !manualBarcode.trim()}
                >
                  <i className="fas fa-search"></i> Look Up
                </Button>
                <Button type="button" onClick={() => setScanMode('realtime')} className="btn-secondary">
                  <i className="fas fa-barcode"></i> Scan Instead
                </Button>
                <Button type="button" onClick={handleClose} className="btn-secondary">
                  <i className="fas fa-times"></i> Cancel
                </Button>
              </div>
            </form>
          </div>
        );
        
      case 'result':
        return (
          <div className="product-result-container">
            <h1>Scanned Product</h1>
            
            {isLoading ? (
              <div className="loading">Looking up product information...</div>
            ) : error ? (
              <div className="error-message">
                <p>{error}</p>
                <div className="button-group">
                  <Button onClick={handleScanAgain} className="btn-primary">
                    <i className="fas fa-barcode"></i> Try Again
                  </Button>
                  <Button onClick={handleClose} className="btn-secondary">
                    <i className="fas fa-times"></i> Cancel
                  </Button>
                </div>
              </div>
            ) : productData && (
              <div className="product-details">
                {productData.image_url && (
                  <div className="product-image">
                    <img src={productData.image_url} alt={productData.product_name} />
                  </div>
                )}
                
                <div className="product-info">
                  <h2>{productData.product_name || 'Unknown Product'}</h2>
                  
                  {productData.barcode && (
                    <p className="product-barcode">
                      <strong>Barcode:</strong> {productData.barcode}
                    </p>
                  )}
                  
                  {productData.brand && (
                    <p className="product-brand">
                      <strong>Brand:</strong> {productData.brand}
                    </p>
                  )}
                  
                  {(productData.item_weight && productData.item_weight_unit) && (
                    <p className="product-weight">
                      <strong>Weight:</strong> {productData.item_weight} {productData.item_weight_unit}
                    </p>
                  )}
                  
                  <div className="button-group">
                    <Button onClick={handleAddToInventory} className="btn-primary">
                      <i className="fas fa-plus"></i> Add to Inventory
                    </Button>
                    <Button onClick={handleScanAgain} className="btn-secondary">
                      <i className="fas fa-barcode"></i> Scan Again
                    </Button>
                    <Button onClick={handleClose} className="btn-secondary">
                      <i className="fas fa-times"></i> Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="barcode-scan-page">
      {renderScanMode()}
    </div>
  );
}

export default BarcodeScanPage;